// @flow

import type { Platform } from '../utils/platform';

type MutationTrigger = {
  src: string, // This should be an enum
};

type SearchMatch = {
  transcriptId: string,
  index: number,
  word: string,
};

type Action =
  | { type: 'CLEAN_APP' }
  | { type: 'META_DOWN', id: number }
  | { type: 'META_UP' }
  | { type: 'DISPLAY_SHORTCUTS' }
  | { type: 'EXIT_INSERT_SPEAKER_NAME_MODE' }
  | { type: 'ENTER_INSERT_SPEAKER_NAME_MODE' }
  | { type: 'ENTER_UPDATE_AUDIO_SOURCE_MODE' }
  | { type: 'EXIT_UPDATE_AUDIO_SOURCE_MODE' }
  | { type: 'SCRIBE_ENTER_SEARCH_MODE' }
  | {
      type: 'SCRIBE_UPDATE_SEARCH_QUERY',
      query: string,
      matches: Array<SearchMatch>,
    }
  | { type: 'SCRIBE_EXIT_SEARCH_MODE' }
  | { type: 'SCRIBE_TRANSCRIPT_DESELECT_WORD' }
  | { type: 'SCRIBE_TRANSCRIPT_SELECT_WORD' }
  | { type: 'SCRIBE_WORD_EDIT_STARTED' }
  | { type: 'SCRIBE_WORD_EDIT_STOPED' }
  | {
      type: 'SWITCH_PLATFORM',
      currentPlatform: Platform,
    }
  | { type: 'SCRIBE_SET_FONT_SIZE', fontSize: number }
  | { type: 'SCRIBE_TRANSCRIPT_FINALIZE_WAIT', transcriptFinalizeWait: number }
  | { type: 'FULL_SCREEN', value: boolean }
  | {
      type: 'RECEIVED_SCRIBE_CONVERSATION_TRANSCRIPT_MUTATION',
      transcriptId: number,
      mutation: ScribeTranscriptMutation,
      trigger: MutationTrigger,
    }
  | { type: 'ELECTRON_CAPTION_MODE', value: boolean };

type State = {
  searchModeEnabled: boolean,
  searchQuery: string,
  mutationTriggers: Array<{
    transcriptId: number,
    mutationId: string,
    src: string,
  }>,
  transcriptFinalizeWait: number,
  fontSize: number,
  wordBeingEdited: boolean,
  delay: number,
  fullScreen: boolean,
  bottomMenu: boolean,
  metaDown: number,
  displayShortcuts: boolean,
  insertSpeakerNameMode: boolean,
  updateAudioSourceMode: boolean,
  electronCaptionMode: boolean,
};

const initialState: State = {
  delay: 1500,
  fontSize: 20,
  mutationTriggers: [],
  searchModeEnabled: false,
  searchQuery: '',
  transcriptFinalizeWait: 9000,
  wordBeingEdited: false,
  fullScreen: window.location.href.includes('fii.ava.me/session-'),
  bottomMenu: false,
  metaDown: 0,
  displayShortcuts: false,
  insertSpeakerNameMode: false,
  updateAudioSourceMode: false,
  electronCaptionMode: false,
};

export default function scribeTranscriptUI(
  state: State = initialState,
  action: Action
): State {
  switch (action.type) {
    case 'RECEIVED_SCRIBE_CONVERSATION_TRANSCRIPT_MUTATION': {
      if (!action.trigger) return state;
      const {
        mutation,
        transcriptId,
        trigger: { src },
      } = action;

      return {
        ...state,
        mutationTriggers: [
          // ...state.mutationTriggers,
          {
            transcriptId,
            mutationId: mutation.id,
            src,
          },
        ],
      };
    }

    case 'SCRIBE_ENTER_SEARCH_MODE':
      return {
        ...state,
        searchModeEnabled: true,
      };

    case 'ENTER_UPDATE_AUDIO_SOURCE_MODE':
      return {
        ...state,
        updateAudioSourceMode: true,
      };

    case 'EXIT_UPDATE_AUDIO_SOURCE_MODE':
      return {
        ...state,
        updateAudioSourceMode: false,
      };

    case 'SCRIBE_EXIT_SEARCH_MODE':
      return {
        ...state,
        searchModeEnabled: false,
        searchQuery: '',
      };

    case 'SCRIBE_UPDATE_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.query,
      };

    case 'SCRIBE_SET_FONT_SIZE':
      return {
        ...state,
        fontSize: action.fontSize,
      };

    case 'SCRIBE_TRANSCRIPT_FINALIZE_WAIT':
      return {
        ...state,
        transcriptFinalizeWait: action.transcriptFinalizeWait,
      };

    case 'SCRIBE_WORD_EDIT_STARTED':
      return {
        ...state,
        wordBeingEdited: true,
      };

    case 'SCRIBE_WORD_EDIT_STOPED':
      return {
        ...state,
        wordBeingEdited: false,
      };

    case 'CLEAN_APP':
      return {
        ...initialState,
      };

    case 'FULL_SCREEN':
      return {
        ...state,
        fullScreen: action.value,
      };

    case 'ELECTRON_CAPTION_MODE':
      return {
        ...state,
        electronCaptionMode: action.value,
      };

    case 'SWITCH_PLATFORM':
      return {
        ...state,
        bottomMenu: action.currentPlatform === 'scribe',
      };

    case 'META_DOWN':
      return {
        ...state,
        metaDown: action.id,
      };

    case 'META_UP':
      return {
        ...state,
        metaDown: 0,
        displayShortcuts: state.insertSpeakerNameMode,
      };

    case 'DISPLAY_SHORTCUTS':
      return {
        ...state,
        displayShortcuts: true,
      };

    case 'ENTER_INSERT_SPEAKER_NAME_MODE':
      return {
        ...state,
        displayShortcuts: true,
        insertSpeakerNameMode: true,
      };

    case 'EXIT_INSERT_SPEAKER_NAME_MODE':
      return {
        ...state,
        displayShortcuts: false,
        insertSpeakerNameMode: false,
      };

    case 'SCRIBE_TRANSCRIPT_SELECT_WORD':
      return {
        ...state,
        wordBeingEdited: false,
      };

    case 'SCRIBE_TRANSCRIPT_DESELECT_WORD':
      return {
        ...state,
        wordBeingEdited: false,
      };

    default:
      // eslint-disable-next-line no-unused-expressions
      (action: empty);
      return state;
  }
}
